import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '@/api/request';

type VisitEventType = 'page_view' | 'page_leave' | 'page_heartbeat';

interface PageSnapshot {
  path: string;
  url: string;
  title: string;
  referer: string;
  startedAt: number;
}

let lastPageViewPath = '';
let lastPageViewAt = 0;
let lastLeaveKey = '';

function randomId(prefix: string) {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getVisitorId() {
  const key = 'hr_visit_id';
  let value = localStorage.getItem(key);
  if (!value) {
    value = randomId('v');
    localStorage.setItem(key, value);
  }
  return value;
}

function getSessionId() {
  const key = 'hr_session_id';
  let value = sessionStorage.getItem(key);
  if (!value) {
    value = randomId('s');
    sessionStorage.setItem(key, value);
  }
  return value;
}

function trackUrl() {
  return `${String(API_BASE_URL).replace(/\/+$/, '')}/analytics/track`;
}

function postTrack(payload: Record<string, unknown>, keepalive = false) {
  const body = JSON.stringify(payload);
  if (keepalive && navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    if (navigator.sendBeacon(trackUrl(), blob)) return;
  }
  fetch(trackUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive,
  }).catch(() => {
    // 访问统计失败不影响门户页面正常访问。
  });
}

function buildPayload(type: VisitEventType, page: PageSnapshot, extra?: Record<string, unknown>) {
  return {
    event_type: type,
    app_id: 'portal_pc',
    path: page.path,
    url: page.url,
    page_title: page.title,
    referer: page.referer,
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    ...extra,
  };
}

function sendLeave(page: PageSnapshot | null, isExit = false) {
  if (!page) return;
  const leaveKey = `${page.path}:${page.startedAt}:${isExit ? 'exit' : 'leave'}`;
  if (lastLeaveKey === leaveKey) return;
  lastLeaveKey = leaveKey;
  const duration = Math.max(0, Math.round((Date.now() - page.startedAt) / 1000));
  postTrack(buildPayload('page_leave', page, {
    duration_seconds: duration,
    is_exit: isExit,
  }), isExit);
}

export function VisitTracker() {
  const location = useLocation();
  const currentPageRef = useRef<PageSnapshot | null>(null);
  const entrySentRef = useRef(false);

  const routeKey = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.pathname, location.search, location.hash],
  );

  useEffect(() => {
    const previous = currentPageRef.current;
    if (previous) sendLeave(previous);

    const now = Date.now();
    const page: PageSnapshot = {
      path: routeKey || '/',
      url: window.location.href,
      title: document.title || '就业服务平台',
      referer: previous?.url || document.referrer || '',
      startedAt: now,
    };
    currentPageRef.current = page;

    const isDuplicateDevHit = lastPageViewPath === page.path && now - lastPageViewAt < 1200;
    if (!isDuplicateDevHit) {
      postTrack(buildPayload('page_view', page, {
        is_entry: !entrySentRef.current,
      }));
      lastPageViewPath = page.path;
      lastPageViewAt = now;
    }
    entrySentRef.current = true;
  }, [routeKey]);

  useEffect(() => {
    const handleHidden = () => {
      if (document.visibilityState === 'hidden') {
        sendLeave(currentPageRef.current, true);
      }
    };
    const handlePageHide = () => sendLeave(currentPageRef.current, true);
    document.addEventListener('visibilitychange', handleHidden);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleHidden);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  return null;
}

export default VisitTracker;
